/**
 * <nia-recipe>
 * Calculadora de la receta de la casa. Dos modos:
 *
 *  · Tamaño fijo — cada botón trae sus cinco cantidades en atributos. Son las
 *    proporciones publicadas del matcha bar, no un cálculo: se imprimen tal
 *    cual para que nadie tenga que confiar en un redondeo.
 *  · A tu medida — se calcula desde los factores por gramo que trae el propio
 *    elemento, todos editables desde el customizer.
 *
 * Ni la receta ni las etiquetas viven aquí: llegan del Liquid en atributos.
 */
const OZ_POR_ML = 29.57;
const DONE_MS = 1800;

/** Redondea al múltiplo de 5 más cercano. Agua, leche y hielo se sirven así. */
const aMultiploDe5 = (n) => Math.round(n / 5) * 5;

/** Un decimal, y sin el .0 colgando cuando el número es redondo. */
const aUnDecimal = (n) => {
  const redondeado = Math.round(n * 10) / 10;
  return String(redondeado);
};

class NiaRecipe extends HTMLElement {
  #gramos = 0;
  #esLibre = false;
  #restaurar = null;

  connectedCallback() {
    this.opciones = Array.from(this.querySelectorAll('[data-rc-size]'));
    this.panel = this.querySelector('[data-rc-panel]');
    this.rendimiento = this.querySelector('[data-rc-yield]');
    this.gramosEl = this.querySelector('[data-rc-grams]');
    this.copiar = this.querySelector('[data-rc-copy]');
    this.estado = this.querySelector('[data-rc-status]');

    this.factores = {
      agua: Number(this.dataset.aguaPorG),
      leche: Number(this.dataset.lechePorG),
      hielo: Number(this.dataset.hieloPorG),
      agaveMin: Number(this.dataset.agaveMin),
      agaveMax: Number(this.dataset.agaveMax),
    };

    this.limites = {
      min: Number(this.dataset.customMin) || 1,
      max: Number(this.dataset.customMax) || 8,
      paso: Number(this.dataset.customStep) || 0.5,
    };

    this.#gramos = Number(this.dataset.customDefault) || this.limites.min;
    this.unidadGramos = this.gramosEl?.textContent.replace(/[\d.,\s]/g, '') || 'g';

    this.addEventListener('click', this.#alHacerClic);
    this.addEventListener('keydown', this.#alTeclear);

    const inicial = this.opciones[0];
    if (inicial) this.#elegir(inicial, { moverFoco: false });
  }

  /** La opción marcada ahora mismo. */
  get #actual() {
    return this.opciones.find((o) => o.getAttribute('aria-checked') === 'true');
  }

  /**
   * Un radiogroup tiene una sola parada de tabulación: la opción marcada. Las
   * demás salen del recorrido y se alcanzan con las flechas.
   */
  #elegir(opcion, { moverFoco = true } = {}) {
    this.opciones.forEach((o) => {
      const activa = o === opcion;
      o.setAttribute('aria-checked', String(activa));
      o.tabIndex = activa ? 0 : -1;
    });

    this.#esLibre = opcion.hasAttribute('data-rc-custom');
    if (this.panel) this.panel.hidden = !this.#esLibre;
    if (moverFoco) opcion.focus();

    this.#pintar();
  }

  #alHacerClic = (evento) => {
    const objetivo = evento.target;
    if (!(objetivo instanceof Element)) return;

    const opcion = objetivo.closest('[data-rc-size]');
    if (opcion) {
      this.#elegir(opcion, { moverFoco: false });
      return;
    }

    const paso = objetivo.closest('[data-rc-step]');
    if (paso instanceof HTMLElement) {
      this.#ajustarGramos(Number(paso.dataset.rcStep) * this.limites.paso);
      return;
    }

    if (objetivo.closest('[data-rc-copy]')) this.#copiar();
  };

  /** Flechas para moverse dentro del radiogroup, Inicio y Fin a los extremos. */
  #alTeclear = (evento) => {
    if (!(evento.target instanceof Element)) return;
    if (!evento.target.closest('[data-rc-size]')) return;

    const total = this.opciones.length;
    const indice = this.opciones.indexOf(this.#actual);
    let siguiente = null;

    switch (evento.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        siguiente = (indice + 1) % total;
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        siguiente = (indice - 1 + total) % total;
        break;
      case 'Home':
        siguiente = 0;
        break;
      case 'End':
        siguiente = total - 1;
        break;
      default:
        return;
    }

    evento.preventDefault();
    this.#elegir(this.opciones[siguiente]);
  };

  #ajustarGramos(delta) {
    const { min, max } = this.limites;
    const crudo = this.#gramos + delta;
    // Se redondea al paso para que sumar y restar no acumule decimales sueltos.
    const alPaso = Math.round(crudo / this.limites.paso) * this.limites.paso;
    this.#gramos = Math.min(Math.max(alPaso, min), max);
    this.#pintar();
  }

  /** Las cinco cantidades de la receta según el modo activo. */
  get #receta() {
    const opcion = this.#actual;
    if (!opcion) return null;

    if (!this.#esLibre) {
      const d = opcion.dataset;
      return {
        matcha: aUnDecimal(Number(d.matcha)),
        agua: aUnDecimal(Number(d.agua)),
        leche: aUnDecimal(Number(d.leche)),
        hielo: aUnDecimal(Number(d.hielo)),
        agave: `${aUnDecimal(Number(d.agaveMin))}–${aUnDecimal(Number(d.agaveMax))}`,
      };
    }

    const g = this.#gramos;
    const f = this.factores;
    return {
      matcha: aUnDecimal(g),
      agua: String(aMultiploDe5(g * f.agua)),
      leche: String(aMultiploDe5(g * f.leche)),
      hielo: String(aMultiploDe5(g * f.hielo)),
      agave: `${aUnDecimal(g * f.agaveMin)}–${aUnDecimal(g * f.agaveMax)}`,
    };
  }

  #pintar() {
    const receta = this.#receta;
    if (!receta) return;

    Object.entries(receta).forEach(([clave, valor]) => {
      const nodo = this.querySelector(`[data-rc-value="${clave}"]`);
      if (nodo) nodo.textContent = valor;
    });

    if (this.#esLibre) {
      if (this.gramosEl) this.gramosEl.textContent = `${aUnDecimal(this.#gramos)} ${this.unidadGramos}`;
      this.#pintarRendimiento();
      this.#limitarStepper();
    }
  }

  /** El mililitro manda y la onza va como referencia, no al revés. */
  #pintarRendimiento() {
    if (!this.rendimiento) return;
    const ml = Math.round((this.#gramos * 100) / 10) * 10;
    const oz = Math.round(ml / OZ_POR_ML);
    const plantilla = this.dataset.yieldTemplate || '{ml} ml · {oz} oz';
    this.rendimiento.textContent = plantilla.replace('{ml}', String(ml)).replace('{oz}', String(oz));
  }

  #limitarStepper() {
    const menos = this.querySelector('[data-rc-step="-1"]');
    const mas = this.querySelector('[data-rc-step="1"]');
    if (menos instanceof HTMLButtonElement) menos.disabled = this.#gramos <= this.limites.min;
    if (mas instanceof HTMLButtonElement) mas.disabled = this.#gramos >= this.limites.max;
  }

  /** Arma la receta en texto plano, con los mismos nombres que se ven. */
  #comoTexto() {
    const titulo = this.dataset.copyTitle || '';
    const tamano = this.#actual?.textContent.trim() ?? '';
    const lineas = [`${titulo} — ${tamano}`.trim(), ''];

    this.querySelectorAll('.nia-rc__row').forEach((fila) => {
      const nombre = fila.querySelector('.nia-rc__name')?.textContent.trim() ?? '';
      const valor = fila.querySelector('.nia-rc__value')?.textContent.trim() ?? '';
      const unidad = fila.querySelector('.nia-rc__unit')?.textContent.trim() ?? '';
      if (nombre) lineas.push(`${nombre}: ${valor} ${unidad}`.trim());
    });

    const pasos = Array.from(this.querySelectorAll('.nia-rc__step-text'));
    if (pasos.length > 0) {
      lineas.push('');
      pasos.forEach((paso, i) => lineas.push(`${i + 1}. ${paso.textContent.trim()}`));
    }

    return lineas.join('\n');
  }

  async #copiar() {
    if (!this.copiar) return;
    const texto = this.#comoTexto();

    try {
      await navigator.clipboard.writeText(texto);
    } catch (error) {
      // Sin permiso de portapapeles o sin contexto seguro: se cae al camino
      // viejo, que sigue funcionando en navegadores que bloquean la API.
      if (!this.#copiarALaAntigua(texto)) return;
    }

    if (this.#restaurar) clearTimeout(this.#restaurar);
    else this.copiar.dataset.rcLabel = this.copiar.textContent.trim();

    this.copiar.textContent = this.dataset.copyDone || 'Copiada';
    if (this.estado) this.estado.textContent = this.dataset.copyDone || 'Copiada';

    this.#restaurar = setTimeout(() => {
      this.copiar.textContent = this.copiar.dataset.rcLabel || this.dataset.copyLabel || '';
      if (this.estado) this.estado.textContent = '';
      this.#restaurar = null;
    }, DONE_MS);
  }

  /** @returns {boolean} si logró copiar */
  #copiarALaAntigua(texto) {
    const area = document.createElement('textarea');
    area.value = texto;
    area.setAttribute('readonly', '');
    area.style.cssText = 'position:fixed;top:-9999px;opacity:0;';
    document.body.appendChild(area);
    area.select();

    let ok = false;
    try {
      ok = document.execCommand('copy');
    } catch (error) {
      ok = false;
    }

    area.remove();
    return ok;
  }
}

if (!customElements.get('nia-recipe')) {
  customElements.define('nia-recipe', NiaRecipe);
}
